/**
 * Axis titles. A collapsible block that says what each Y axis title of a line
 * or scatter chart reads: Auto (the first three series and "(+N)"), Signals
 * (only the picked series), Text (the user's own words) or Off. Same shape as
 * Axis bounds; the header shows a `(N set)` count.
 *
 * A title is drawing, not data, so a change redraws from the cached response
 * and never re-queries. The values live in `plotState.axis_titles`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  plotState,
  setAxisTitle,
  axisTitlesActiveCount,
  type AxisTitleMode,
  type AxisTitles,
  type PlotState,
} from '../../state/plot-state.ts';
import { rerenderChartFromLast } from './plot-actions.ts';
import { seriesColor } from './plot-constants.ts';
import { joinLabels } from './render/uplot-helpers.ts';

const MODES: Array<{ value: AxisTitleMode; label: string; title: string }> = [
  { value: 'auto', label: 'Auto', title: 'The first three series, then (+N)' },
  { value: 'signals', label: 'Signals', title: 'Only the series you pick' },
  { value: 'text', label: 'Text', title: 'Your own title' },
  { value: 'off', label: 'Off', title: 'No title' },
];

@customElement('axis-titles')
export class AxisTitlesControl extends LightElement {
  @state() private collapsed = true;
  private store = new StoreController(this, [plotState]);

  private set(axis: keyof AxisTitles, patch: Parameters<typeof setAxisTitle>[1]): void {
    setAxisTitle(axis, patch);
    rerenderChartFromLast();
  }

  private toggle(axis: keyof AxisTitles, name: string): void {
    const picked = plotState.value.axis_titles[axis].signals;
    this.set(axis, { signals: picked.includes(name) ? picked.filter((n) => n !== name) : [...picked, name] });
  }

  private renderAxis(s: PlotState, axis: keyof AxisTitles, label: string, series: string[]): TemplateResult {
    const t = s.axis_titles[axis];
    const onKey = (e: KeyboardEvent): void => {
      const i = MODES.findIndex((m) => m.value === t.mode);
      const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = MODES[(i + step + MODES.length) % MODES.length]!.value;
      this.set(axis, { mode: next });
      void this.updateComplete.then(() =>
        this.querySelector<HTMLElement>(`[data-axis="${axis}"][data-mode="${next}"]`)?.focus(),
      );
    };
    let body: TemplateResult;
    if (t.mode === 'signals') {
      body = html`<div class="axis-title-picks">
        ${series.map((name) => {
          const on = t.signals.includes(name);
          return html`<button
            type="button"
            class="axis-title-pick"
            aria-pressed=${on ? 'true' : 'false'}
            @click=${() => this.toggle(axis, name)}
          ><span class="axis-title-dot" style="background:${seriesColor(s, name)}"></span>${name}</button>`;
        })}
      </div>`;
    } else if (t.mode === 'text') {
      body = html`<input
        type="text"
        class="axis-title-input"
        maxlength="80"
        aria-label="${label} title"
        placeholder=${joinLabels(series)}
        .value=${t.text}
        @input=${(e: Event) => this.set(axis, { text: (e.target as HTMLInputElement).value })}
      />`;
    } else {
      body = html`<div class="toggle-hint">${t.mode === 'off' ? 'No title. The numbers stay.' : joinLabels(series)}</div>`;
    }
    return html`<div class="axis-title-row">
      <div class="axis-title-head">
        <label>${label}</label>
        <div class="seg" role="radiogroup" aria-label="${label} title" @keydown=${onKey}>
          ${MODES.map(({ value, label: text, title }) => html`<button
            type="button"
            class="seg-btn ${t.mode === value ? 'active' : ''}"
            role="radio"
            data-axis=${axis}
            data-mode=${value}
            aria-checked=${t.mode === value ? 'true' : 'false'}
            tabindex=${t.mode === value ? 0 : -1}
            title=${title}
            @click=${() => this.set(axis, { mode: value })}
          >${text}</button>`)}
        </div>
      </div>
      ${body}
    </div>`;
  }

  override render(): TemplateResult {
    const s = plotState.value;
    if (s.plot_type !== 'line' && s.plot_type !== 'scatter') return html`${nothing}`;
    const n = axisTitlesActiveCount(s);
    return html`
      <div class="axis-bounds-wrap">
        <div class="section-header" @click=${() => (this.collapsed = !this.collapsed)}>
          <span class="arrow ${this.collapsed ? '' : 'open'}">▶</span>
          <span>Axis titles</span>
          <span class="count">${n > 0 ? `(${n} set)` : ''}</span>
        </div>
        <div class="section-body ${this.collapsed ? 'collapsed' : ''}">
          ${this.renderAxis(s, 'y', s.y_right.length ? 'Y left' : 'Y', s.y_left)}
          ${s.y_right.length ? this.renderAxis(s, 'yr', 'Y right', s.y_right) : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'axis-titles': AxisTitlesControl;
  }
}
